'use client';

import {
  useState, useRef, useMemo, useCallback, useEffect,
} from 'react';
import { useTranslation } from 'next-i18next';
import {
  PaperAirplaneIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon,
} from '@heroicons/react/24/solid';
import { ChatTypingDots, Button } from '@components/common';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import { Message, useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { v4 as uuidv4 } from 'uuid';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import ChatTalentModal from '@components/templates/homepage/chat/ChatTalentModal';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';


const agent = { id: '' };

interface MissionControlHireContentProps {
  locale: Locale;
}

/**
 * @description Hire chat experience for the Mission Control company (hiring) view.
 * Replicates the /hire page hero — title + input + chips — but rendered inside
 * the MC content column (not the full-page landing layout).
 * Opens the chat inline, the same way MissionControlBestieContent does.
 */
export const MissionControlHireContent = ({ locale }: MissionControlHireContentProps) => {
  const { t } = useTranslation('common');

  const { stream, startStream } = useStreamStateV2(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const anonSessionId = useAnonSessionId();
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);

  const headers = useMemo(() => ({
    'x-anon-session-id': anonSessionId ?? '',
  }), [anonSessionId]);

  const sendMessage = useCallback(async (text: string) => {
    const message = { id: uuidv4(), content: text, type: 'human' } as const;
    await startStream(message, agent.id, {
      headers,
      configurations: { user_locale: locale || 'ro' },
    });
  }, [startStream, headers, locale]);

  const composer = useChatComposer({
    history: stream?.messages,
    onSubmit: (text) => { void sendMessage(text); },
  });

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);
  useEffect(() => {
    if (bulkApplyFeed?.length) {
      NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale }).catch(() => {});
    }
  }, [bulkApplyFeed, locale]);

  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (item?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0)
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
    acc.push({ items: item?.response_metadata?.customData?.jobSearchResults?.items?.map((i) => i.data) });
    return acc;
  }, []), [stream?.messages]);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];
    return [...new Map(stream.messages.map((m) => [m.id, m])).values()]
      .reverse()
      .map((m, index) => (
        <div key={`message-container-${m.id}`} className="flex flex-col gap-4">
          <ChatMessageComponent
            key={`message-${m.id}`}
            message={{ ...m }}
            ref={index === 0 ? lastMessageRef : null}
            locale={locale}
            onInteraction={onCvDetailOpen}
          />
        </div>
      ));
  }, [stream?.messages, locale, onCvDetailOpen]);

  const shouldShowChatView = (stream?.messages?.length ?? 0) > 0;

  const [expandState, setExpandState] = useState<{
    showToggle: boolean; isExpanded: boolean; toggle:() => void;
  } | null>(null);

  const formElements = (
    <div className="mt-6 w-full max-w-5xl">
      <div className="flex w-full items-end gap-2 rounded-[24px] bg-surface p-6 shadow-lg backdrop-blur-sm transition-all duration-700">
        <ChatTextarea
          required={false}
          textareaRef={composer.textareaRef}
          input={composer.input}
          handleInputChange={composer.onChange}
          handleKeyDown={composer.onKeyDown}
          placeholder={shouldShowChatView ? t('homepage.chat.input.placeholder') : t('bestie.hire.hero.input_placeholder')}
          customWrapperStyle="flex-1 rounded-2xl"
          customTextareaStyle="transition-none bg-transparent !text-ink placeholder:!text-ink/75 placeholder:opacity-100 placeholder:text-base placeholder:sm:text-xl px-2 text-base sm:text-lg focus:ring-0 focus:ring-offset-0 outline-none border-none shadow-none whitespace-normal resize-none cursor-text pointer-events-auto leading-normal py-1.5 break-words"
          textareaSizeStyle="w-full min-h-10"
          textareaWrapperSizeStyle="w-full min-h-10"
          textareaAlignmentStyle="px-2 py-2.5"
          textareaWrapperAlignmentStyle="m-0"
          collapsedLineLimit={6}
          expandedLineLimit={15}
          onExpandStateChange={setExpandState}
        />
        <div className="flex shrink-0 flex-col items-center self-stretch">
          {expandState?.showToggle && (
            <button
              type="button"
              onClick={expandState.toggle}
              className="mt-1 shrink-0 rounded p-1 text-ink transition-colors hover:text-ink"
              aria-label={expandState.isExpanded ? 'Collapse' : 'Expand'}
            >
              {expandState.isExpanded
                ? <ArrowsPointingInIcon className="size-5" />
                : <ArrowsPointingOutIcon className="size-5" />}
            </button>
          )}
          <Button
            type="button"
            styling="outline"
            color="light"
            rounding="none"
            className="mt-auto !h-11 !w-11 shrink-0 !rounded-full bg-secondary !p-0 transition-transform hover:scale-110 hover:!bg-secondary"
            onClick={() => { void composer.submit(); }}
          >
            <PaperAirplaneIcon className="size-5 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-full flex-col bg-surface-100">
      {shouldShowChatView ? (
        <div className="flex min-h-0 grow flex-col">
          <div
            ref={messageContainerRef}
            className="container mx-auto flex min-h-0 grow flex-col-reverse gap-5 overflow-y-auto p-4 pb-6"
          >
            {(stream?.status === 'inflight') && <ChatTypingDots />}
            {messagesList}
          </div>

          <div className="bg-surface-100/95 backdrop-blur-sm">
            <form
              onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
              className="container mx-auto px-4 pb-4 pt-3"
            >
              <div className="mx-auto w-full max-w-4xl">
                {formElements}
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="flex grow flex-col items-center justify-end pb-2 pt-6 lg:pt-16">
          <div className="flex w-full max-w-5xl flex-col items-center gap-4 px-4 text-center sm:px-6 lg:px-0">
            <span className="mb-2 w-full text-4xl font-bold leading-snug sm:text-5xl lg:text-[72px] lg:leading-tight">
              {t('hr.hero.title')}
            </span>
            <span className="w-full max-w-4xl text-base !text-ink/75 sm:text-lg lg:text-2xl">
              {t('hr.hero.subtitle')}
            </span>

            <form
              onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
              className="w-full max-w-4xl px-7"
            >
              {formElements}
            </form>
          </div>
        </div>
      )}

      <ChatJobModal locale={locale} jobsArray={jobsArray} />
      <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
    </div>
  );
};
